import { useAppData } from "@/contexts/AppDataContext";
import { useGetSeoStrategyRecommendations } from "@/resources/seo-strategy";
import { useListScorecards } from "@/resources/scorecard";
import { strategyRunApi } from "@/resources/strategy-run";
import { isRunInFlight } from "@/utils/landscapeRunStatus";
import { StrategyBrowser, type StrategyBrowserProps } from "./StrategyBrowser";
import { StrategyCardSkeleton } from "./StrategyCardSkeleton";
import { StrategyBuildingState, StrategyErrorState, StrategyNeverPulledState } from "./StrategyTabStates";

type StrategyBrowserStatesProps = Omit<StrategyBrowserProps, "asin" | "productId" | "brandName"> & {
  asin: string;
  productId: number;
  brandName: string;
  /**
   * AUT-196: when given, the never-pulled state offers the design-QA "Generate Amazon AI Score"
   * button. The Strategy tab passes one; `CreateTestModal` -- the second host this component
   * serves -- passes none, because navigating away from a half-written test is not what that
   * screen wants. A prop rather than `useNavigate` so this component stays router-free, which
   * `CreateTestModal`'s own tests rely on.
   */
  onGenerateScore?: () => void;
};

/**
 * AUT-430 spec C / WEB-001: the tab-level state machine, moved out of
 * `SeoStrategyTab.renderBody()` so a second host (A/B creation's `Strategies` build tab,
 * `WEB-011`) can render the same eight states without a second copy
 * (`decisions/the-strategy-browser-takes-its-ab-creation-action-and-segment-as-props.md`
 * decision 3). Same conditions, same order, same copy, same two queries as before the
 * move -- `components/seo-strategy.md § The screen`, evaluated top to bottom, first match
 * wins:
 *
 *   1. Fetching (recommendations first load)         -> skeleton, no alert
 *   2. Error, nothing to show (recommendations errored, all four lists empty) -> the never-pulled
 *      shell + AlertCircle
 *   2b. Error, but a list is already on screen (a background refetch failed) -> stays on the
 *       browser with the stale data, silently
 *   3. Loaded (any of the four lists non-empty)        -> the browser
 *   3b. Generation in flight (all empty, the run is queued/running/stopped) -> loading
 *       alert + skeleton -- AUT-196, below
 *   4. Fetching (all empty, the audit list's first load) -> skeleton
 *   5. Error (all empty, the audit list errored)       -> as (2)
 *   6. Loaded, empty (an audit has completed)          -> the browser
 *   7. Building (an audit is pending/processing)       -> loading alert + skeleton
 *   8. Never pulled (no audit at all)                  -> the dashed empty shell
 *
 * The audit list (`useListScorecards`) is a fallback read only, consulted with an
 * `undefined` ASIN -- and so disabled -- until the recommendations response is in and all
 * four of its lists are empty (implementation guidance, `scorecard.api.ts`).
 *
 * AUT-196 adds step 3b. The audit list cannot see generation: the job runs *after* the
 * audit completes, so an audit-completed ASIN whose generation is still running falls to
 * step 6 and reads as "loaded, empty" -- an empty board, not a building one. The
 * `seo_strategy` work run is the authoritative signal for that window
 * (`components/work-run-log.md § The third kind -- SEO strategy runs`,
 * `decisions/a-seo-strategy-run-is-recorded-from-inside-its-job.md`), so it is consulted
 * ahead of the audit fallback and the audit keeps deciding every case it already decided.
 * `useGetStrategyRun` also owns the invalidation that flips this tab from building to the
 * real recommendations when the run ends (`strategy-run.api.ts`), which is why it is called
 * unconditionally rather than gated on the board being empty.
 *
 * There is deliberately no `failed` branch: a settled run of any terminal status is not in
 * flight, so a thrown run falls through to the audit fallback and reads as it did before
 * (`specs/product/strategy-tab-loading-and-empty-states/intent.md § Out of scope`).
 *
 * `asin`, `productId` and `brandName` are required; every other prop is `StrategyBrowser`'s
 * own optional prop, passed through unchanged to whichever host mounts this component.
 */
export function StrategyBrowserStates({ asin, productId, brandName, onGenerateScore, ...browserProps }: StrategyBrowserStatesProps) {
  const { selectedBrandId } = useAppData();
  const {
    data: recommendationsData,
    isPending: recommendationsPending,
    isError: recommendationsError,
    refetch: refetchRecommendations,
  } = useGetSeoStrategyRecommendations(asin);

  const hasAnyRecommendation =
    !!recommendationsData &&
    (recommendationsData.pending.length > 0 ||
      recommendationsData.prioritized.length > 0 ||
      recommendationsData.completed.length > 0 ||
      recommendationsData.dismissed.length > 0);

  // Enabled only once the recommendations response is in and empty -- a listing that has
  // any strategy never issues this second request.
  const recommendationsSettledEmpty = !recommendationsPending && !recommendationsError && !hasAnyRecommendation;
  const {
    data: scorecards,
    isPending: scorecardsPending,
    isError: scorecardsError,
    refetch: refetchScorecards,
  } = useListScorecards(recommendationsSettledEmpty ? asin : undefined, selectedBrandId ?? undefined);

  // Polls only while the run it loaded is in flight, and settles to no interval on every
  // ending (`computeStrategyRunRefetchInterval`).
  const { data: strategyRunData } = strategyRunApi.useGetStrategyRun(productId);

  const browser = <StrategyBrowser asin={asin} productId={productId} brandName={brandName} {...browserProps} />;

  if (recommendationsPending) return <StrategyCardSkeleton />;
  // A failed background refetch keeps v5's `data` but sets isError; while cards are on screen,
  // stay on them so open edit drafts survive.
  if (recommendationsError && !hasAnyRecommendation) {
    return <StrategyErrorState onRetry={() => refetchRecommendations()} />;
  }
  if (hasAnyRecommendation) return browser;

  // All four lists are empty from here on. AUT-196: the generation run is consulted first
  // -- it is the only signal that distinguishes "generating right now" from the audit
  // list's "an audit completed, so there is nothing more to wait for".
  if (isRunInFlight(strategyRunData?.run?.status ?? null)) return <StrategyBuildingState />;

  // The audit list is the tiebreaker for everything the run does not settle: never pulled,
  // building (the audit itself) and loaded-but-nothing-to-show.
  if (scorecardsPending) return <StrategyCardSkeleton />;
  if (scorecardsError) {
    return <StrategyErrorState onRetry={() => refetchScorecards()} />;
  }

  const hasCompletedScorecard = (scorecards ?? []).some((scorecard) => scorecard.status === "completed");
  if (hasCompletedScorecard) return browser;

  const hasActiveScorecard = (scorecards ?? []).some(
    (scorecard) => scorecard.status === "pending" || scorecard.status === "processing",
  );
  if (hasActiveScorecard) return <StrategyBuildingState />;

  // AUT-196: the copy tells the user to generate an Amazon AI score, so the state offers the
  // button -- but only where a handler is given. `CreateTestModal` renders this same component
  // inside a dialog and passes none, which also keeps this component router-free, as its own
  // tests mount it.
  return <StrategyNeverPulledState onGenerate={onGenerateScore} />;
}
